import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as AppointmentActions from './appointment.actions';
import { AppointmentEntity } from './appointment.models';

export const APPOINTMENT_FEATURE_KEY = 'appointment';

export interface State extends EntityState<AppointmentEntity> {
  selectedId?: string; // which Appointment record has been selected
  isInitialized: boolean;
  loading: boolean; //  if the appiontment list is loading
  updating: boolean;
  creating: boolean;
  pulling: boolean;
  error?: string | null; // last none error (if any)
}

export interface AppointmentPartialState {
  readonly [APPOINTMENT_FEATURE_KEY]: State;
}

export function selectAppointmentId(appointment: AppointmentEntity): string {
  //In this case this would be optional since primary key is id
  return appointment._id;
}

export const appointmentAdapter: EntityAdapter<AppointmentEntity> =
  createEntityAdapter<AppointmentEntity>({
    selectId: selectAppointmentId
  });

export const initialState: State = appointmentAdapter.getInitialState({
  // set initial required properties
  selectedId: null,
  isInitialized: false,
  loading: false,
  updating: false,
  creating: false,
  pulling: false
});

const appointmentReducer = createReducer(
  initialState,
  /* ----------------------------- Get Appointment By Id ----------------------------- */

  on(AppointmentActions.getAppointmentById, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AppointmentActions.getAppointmentByIdSuccess, (state, { appointment }) =>
    appointmentAdapter.upsertOne(appointment, {
      ...state,
      loading: false,
      error: null,
      selectedId: appointment._id
    })
  ),
  on(AppointmentActions.getAppointmentByIdFailure, (state, { error }) => ({
    ...state,
    error: error.message || 'Unknown Error',
    loading: false,
    selectedId: null
  })),
  on(AppointmentActions.stitchInit, state => ({
    ...state,
    isInitialized: false,
    loading: true,
    error: null
  })),
  on(AppointmentActions.stitchInitSuccess, state => ({
    ...state,
    isInitialized: true,
    loading: false
  })),
  on(AppointmentActions.stitchInitFailure, (state, { error }) => ({
    ...state,
    isInitialized: false,
    loading: false,
    error
  })),

  on(AppointmentActions.loadAppointments, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AppointmentActions.loadAppointmentsSuccess, (state, { appointments }) =>
    appointmentAdapter.setAll(appointments, { ...state, loading: false })
  ),
  on(AppointmentActions.loadAppointmentsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  on(AppointmentActions.fetchMySeenTodayAppointments, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    AppointmentActions.fetchMySeenTodayAppointmentsSuccess,
    (state, { appointments }) => {
      const inProgressApt = appointments.find(
        apt => apt.status === 'in-progress'
      );

      return appointmentAdapter.setAll(appointments, {
        ...state,
        loading: false,
        selectedId: inProgressApt ? inProgressApt._id : state.selectedId
      });
    }
  ),
  on(
    AppointmentActions.fetchMySeenTodayAppointmentsFailure,
    (state, { error }) => ({
      ...state,
      loading: false,
      error
    })
  ),

  on(AppointmentActions.saveAppointment, state => ({
    ...state,
    creating: true,
    error: null
  })),
  on(AppointmentActions.saveAppointmentSuccess, (state, { appointment }) => {
    return appointmentAdapter.addOne(appointment, {
      ...state,
      creating: false
    });
  }),
  on(AppointmentActions.saveAppointmentFailure, (state, { error }) => ({
    ...state,
    creating: false,
    error
  })),

  on(AppointmentActions.createAppointment, state => ({
    ...state,
    creating: true,
    error: null
  })),
  on(AppointmentActions.createAppointmentSuccess, (state, { appointment }) => {
    return appointmentAdapter.upsertOne(appointment, {
      ...state,
      creating: false
    });
  }),
  on(AppointmentActions.createAppointmentFailure, (state, { error }) => ({
    ...state,
    creating: false,
    error
  })),

  on(AppointmentActions.updateAppointment, state => ({
    ...state,
    updating: true,
    error: null
  })),
  on(AppointmentActions.updateAppointmentSuccess, (state, { appointment }) => {
    return appointmentAdapter.upsertOne(appointment, {
      ...state,
      updating: false
    });
  }),
  on(AppointmentActions.updateAppointmentFailure, (state, { error }) => ({
    ...state,
    updating: false,
    error
  })),

  on(AppointmentActions.pullAppointment, state => ({
    ...state,
    pulling: true,
    selectedId: undefined,
    conversation: null,
    error: null
  })),

  on(AppointmentActions.pullAppointmentSuccess, (state, { appointment }) => {
    if (!appointment)
      return {
        ...state,
        pulling: false,
        selectedId: undefined
      };

    return appointmentAdapter.upsertOne(appointment, {
      ...state,
      pulling: false,
      selectedId: appointment._id
    });
  }),

  on(AppointmentActions.pullAppointmentFailure, (state, { error }) => ({
    ...state,
    pulling: false,
    conversation: null,
    error
  })),
  on(AppointmentActions.clearAppointment, state => ({
    ...state,
    selectedId: null
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return appointmentReducer(state, action);
}
